import React, { useState, useEffect } from 'react';
import {
  Box, Typography, CircularProgress, Alert,
  Chip, Button, IconButton, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField, Grid,
} from '@mui/material';
import {
  LocalFireDepartment, EmojiEvents, ArrowBack,
  Add, CheckCircle, TrendingUp, Whatshot,
} from '@mui/icons-material';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

const API_BASE = 'https://imara-production-91ab.up.railway.app';

function getAuthHeaders() {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function toDateStr(date) {
  return date.toISOString().split('T')[0];
}

const today = toDateStr(new Date());

function getStoredCheckins() {
  try {
    const s = JSON.parse(localStorage.getItem('imara_checkins') || '{}');
    return s.date === today ? s.ids || {} : {};
  } catch { return {}; }
}

const MILESTONES = [
  { days: 3,   label: '3-Day Spark',     emoji: '⚡' },
  { days: 7,   label: 'Week Warrior',    emoji: '🛡️' },
  { days: 14,  label: 'Fortnight Force', emoji: '💪' },
  { days: 30,  label: 'Monthly Master',  emoji: '🏆' },
  { days: 60,  label: '2-Month Legend',  emoji: '🌟' },
  { days: 100, label: 'Century Club',    emoji: '💯' },
];

const PRESET_TYPES = [
  'Sobriety', 'Exercise', 'Reading', 'Journaling',
  'Prayer', 'Meditation', 'No Social Media', 'Sleep by 10pm',
];

function flameColor(count) {
  if (count >= 30) return '#ff4500';
  if (count >= 7)  return '#ff6b35';
  if (count >= 3)  return '#ffa040';
  return '#4ade80';
}

function getLast12Weeks() {
  const weeks = [];
  const start = new Date();
  start.setHours(0, 0, 0, 0);
  start.setDate(start.getDate() - start.getDay() - 7 * 11);
  for (let w = 0; w < 12; w++) {
    const week = [];
    for (let d = 0; d < 7; d++) {
      const date = new Date(start);
      date.setDate(start.getDate() + w * 7 + d);
      week.push(date);
    }
    weeks.push(week);
  }
  return weeks;
}

const MONTH_NAMES = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
const DAY_LABELS  = ['S','M','T','W','T','F','S'];
const weeks = getLast12Weeks();

export default function Streaks() {
  const navigate = useNavigate();
  const [streaks, setStreaks] = useState([]);
  const [checkinDates, setCheckinDates] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [checkedInToday, setCheckedInToday] = useState(getStoredCheckins);
  const [checkingIn, setCheckingIn] = useState({});
  const [newStreakOpen, setNewStreakOpen] = useState(false);
  const [newStreakType, setNewStreakType] = useState('');
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) { navigate('/'); return; }
    loadData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function loadData() {
    try {
      setLoading(true);
      const [streakRes, moodRes] = await Promise.all([
        axios.get(`${API_BASE}/streaks/`, { headers: getAuthHeaders() }).catch(() => ({ data: [] })),
        axios.get(`${API_BASE}/moods/`,   { headers: getAuthHeaders() }).catch(() => ({ data: [] })),
      ]);

      setStreaks(streakRes.data || []);

      // Only use localStorage for today's check-ins
      // Don't use updated_at — it equals today even on newly created streaks
      setCheckedInToday(getStoredCheckins());

      const dates = new Set();
      const moodList = Array.isArray(moodRes.data) ? moodRes.data : [];
      moodList.forEach((m) => {
        if (m.created_at) dates.add(m.created_at.split('T')[0]);
      });
      setCheckinDates(dates);
    } catch {
      setError('Could not load streak data. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  async function handleCheckIn(streakId) {
    setCheckingIn((p) => ({ ...p, [streakId]: true }));
    try {
      await axios.post(`${API_BASE}/streaks/${streakId}/checkin`, {}, { headers: getAuthHeaders() });

      // Save to localStorage immediately
      setCheckedInToday((p) => {
        const updated = { ...p, [streakId]: true };
        localStorage.setItem('imara_checkins', JSON.stringify({ date: today, ids: updated }));
        return updated;
      });

      // Reload from API to get real updated count
      const streakRes = await axios.get(`${API_BASE}/streaks/`, { headers: getAuthHeaders() });
      setStreaks(streakRes.data || []);

    } catch (err) {
      const msg = err?.response?.data?.detail || '';
      if (msg.toLowerCase().includes('already')) {
        setCheckedInToday((p) => {
          const updated = { ...p, [streakId]: true };
          localStorage.setItem('imara_checkins', JSON.stringify({ date: today, ids: updated }));
          return updated;
        });
      }
    } finally {
      setCheckingIn((p) => ({ ...p, [streakId]: false }));
    }
  }

  async function handleCreateStreak() {
    if (!newStreakType.trim()) return;
    setCreating(true);
    try {
      await axios.post(`${API_BASE}/streaks/`, { streak_type: newStreakType.trim().toLowerCase() }, { headers: getAuthHeaders() });
      setNewStreakOpen(false);
      setNewStreakType('');
      await loadData();
    } catch {
      setError('Could not create streak. Try again.');
    } finally {
      setCreating(false);
    }
  }

  const longestStreak = streaks.reduce((max, s) => Math.max(max, s.longest_count || s.current_count || 0), 0);
  const bestCurrent   = streaks.reduce((max, s) => Math.max(max, s.current_count || 0), 0);

  return (
    <Box sx={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #0a1628, #0d1f3c, #0a2040)',
      px: { xs: 2, sm: 3 },
      py: 3, pb: 6,
      color: '#e8f4f8',
    }}>

      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton onClick={() => navigate('/dashboard')} sx={{ color: '#7fa8c8', p: 0.5 }}>
            <ArrowBack />
          </IconButton>
          <Typography sx={{ fontWeight: 800, fontSize: '1.3rem',
            background: 'linear-gradient(90deg, #4ade80, #22d3ee)',
            WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
            Your Streaks
          </Typography>
        </Box>
        <Button onClick={() => setNewStreakOpen(true)} startIcon={<Add />}
          sx={{
            background: 'linear-gradient(135deg, #4ade80, #22d3ee)',
            color: '#0a1628', fontWeight: 700, borderRadius: 2,
            textTransform: 'none', fontSize: '0.82rem', px: 2, py: 0.8,
          }}>
          New Streak
        </Button>
      </Box>

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
          <CircularProgress sx={{ color: '#4ade80' }} />
        </Box>
      )}
      {error && !loading && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {!loading && (
        <>
          {/* Summary row */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            {[
              { icon: <Whatshot sx={{ color: '#f97316', fontSize: 22 }} />, label: 'Best Current', value: `${bestCurrent}d` },
              { icon: <EmojiEvents sx={{ color: '#fbbf24', fontSize: 22 }} />, label: 'Longest Ever', value: `${longestStreak}d` },
              { icon: <LocalFireDepartment sx={{ color: '#4ade80', fontSize: 22 }} />, label: 'Total Streaks', value: streaks.length },
            ].map((s, i) => (
              <Grid item xs={4} key={i}>
                <Box sx={{
                  background: 'rgba(255,255,255,0.04)',
                  border: '1px solid rgba(255,255,255,0.08)',
                  borderRadius: 3, p: 1.5, textAlign: 'center',
                }}>
                  {s.icon}
                  <Typography sx={{ color: '#e8f4f8', fontWeight: 800, fontSize: '1.3rem' }}>{s.value}</Typography>
                  <Typography sx={{ color: '#7fa8c8', fontSize: '0.65rem' }}>{s.label}</Typography>
                </Box>
              </Grid>
            ))}
          </Grid>

          {/* No streaks */}
          {streaks.length === 0 && (
            <Box sx={{ textAlign: 'center', mt: 6 }}>
              <Typography sx={{ fontSize: '3rem', mb: 1 }}>🔥</Typography>
              <Typography sx={{ color: '#7fa8c8', mb: 2 }}>No streaks yet. Start your first one!</Typography>
              <Button onClick={() => setNewStreakOpen(true)} startIcon={<Add />}
                sx={{ background: 'linear-gradient(135deg, #4ade80, #22d3ee)', color: '#0a1628', fontWeight: 700, borderRadius: 2, textTransform: 'none' }}>
                Create Streak
              </Button>
            </Box>
          )}

          {/* Per-streak cards */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 3 }}>
            {streaks.map((streak) => {
              const count = streak.current_count || 0;
              const done = checkedInToday[streak.id];
              const busy = checkingIn[streak.id];
              const color = flameColor(count);
              const next = MILESTONES.find((m) => count < m.days);

              return (
                <Box key={streak.id} sx={{
                  background: 'rgba(13,31,60,0.9)',
                  border: done ? '1px solid rgba(74,222,128,0.4)' : '1px solid rgba(255,255,255,0.08)',
                  borderRadius: 3, p: 2.5, transition: 'border 0.2s',
                }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                    <Box>
                      <Typography sx={{ color: '#e8f4f8', fontWeight: 700, fontSize: '1rem', textTransform: 'capitalize' }}>
                        {streak.streak_type}
                      </Typography>
                      {next && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                          <TrendingUp sx={{ color: '#4ade80', fontSize: 13 }} />
                          <Typography sx={{ color: '#4ade80', fontSize: '0.72rem' }}>
                            {next.days - count} days to {next.label} {next.emoji}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                    <Box sx={{ textAlign: 'center' }}>
                      <LocalFireDepartment sx={{ color, fontSize: 28, filter: `drop-shadow(0 0 6px ${color})` }} />
                      <Typography sx={{ color: '#fff', fontWeight: 900, fontSize: '1.8rem', lineHeight: 1 }}>
                        {count}
                      </Typography>
                      <Typography sx={{ color: '#7fa8c8', fontSize: '0.65rem', letterSpacing: 1 }}>DAYS</Typography>
                    </Box>
                  </Box>

                  {done ? (
                    <Box sx={{
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      gap: 1, py: 1.2,
                      background: 'rgba(74,222,128,0.1)',
                      border: '1px solid rgba(74,222,128,0.3)',
                      borderRadius: 2,
                    }}>
                      <CheckCircle sx={{ color: '#4ade80', fontSize: 20 }} />
                      <Typography sx={{ color: '#4ade80', fontWeight: 700, fontSize: '0.88rem' }}>
                        Checked in today ✅
                      </Typography>
                    </Box>
                  ) : (
                    <Button fullWidth onClick={() => handleCheckIn(streak.id)} disabled={busy}
                      sx={{
                        background: 'linear-gradient(135deg, #4ade80, #22d3ee)',
                        color: '#0a1628', fontWeight: 800, borderRadius: 2,
                        textTransform: 'none', fontSize: '0.9rem', py: 1.2,
                        '&:hover': { opacity: 0.9 },
                        '&:disabled': { opacity: 0.5 },
                      }}>
                      {busy ? <CircularProgress size={20} sx={{ color: '#0a1628' }} /> : '✓  Check In Today'}
                    </Button>
                  )}
                </Box>
              );
            })}
          </Box>

          {/* Heatmap */}
          <Box sx={{
            background: 'rgba(255,255,255,0.04)',
            border: '1px solid rgba(255,255,255,0.08)',
            borderRadius: 3, p: 2.5, mb: 3, overflowX: 'auto',
          }}>
            <Typography sx={{ fontWeight: 700, mb: 2, color: '#e8f4f8', fontSize: '0.9rem' }}>
              Mood Activity — Last 12 Weeks
            </Typography>
            <Box sx={{ display: 'flex', gap: '3px', mb: 0.5, ml: '18px' }}>
              {weeks.map((week, wi) => (
                <Box key={wi} sx={{ width: 13, flexShrink: 0, fontSize: '0.55rem', color: '#7fa8c8', textAlign: 'center' }}>
                  {week[0].getDate() <= 7 ? MONTH_NAMES[week[0].getMonth()] : ''}
                </Box>
              ))}
            </Box>
            <Box sx={{ display: 'flex', gap: '3px' }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: '3px', mr: 0.5 }}>
                {DAY_LABELS.map((d, i) => (
                  <Box key={i} sx={{ height: 13, width: 13, fontSize: '0.5rem', color: '#7fa8c8', lineHeight: '13px', textAlign: 'center' }}>
                    {d}
                  </Box>
                ))}
              </Box>
              {weeks.map((week, wi) => (
                <Box key={wi} sx={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
                  {week.map((date, di) => {
                    const ds = toDateStr(date);
                    const isToday = ds === today;
                    const active = checkinDates.has(ds);
                    const future = ds > today;
                    return (
                      <Box key={di} title={ds} sx={{
                        width: 13, height: 13, borderRadius: '2px', flexShrink: 0,
                        background: future ? 'rgba(255,255,255,0.02)' : active ? 'linear-gradient(135deg, #4ade80, #22d3ee)' : 'rgba(255,255,255,0.07)',
                        border: isToday ? '1.5px solid #4ade80' : '1px solid transparent',
                      }} />
                    );
                  })}
                </Box>
              ))}
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1.5 }}>
              <Typography sx={{ color: '#3a5a7a', fontSize: '0.65rem' }}>Less</Typography>
              {['rgba(255,255,255,0.07)', 'rgba(74,222,128,0.4)', 'rgba(74,222,128,0.7)', '#4ade80'].map((bg, i) => (
                <Box key={i} sx={{ width: 10, height: 10, borderRadius: '2px', background: bg }} />
              ))}
              <Typography sx={{ color: '#3a5a7a', fontSize: '0.65rem' }}>More</Typography>
            </Box>
          </Box>

          {/* Milestones */}
          <Box sx={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 3, p: 2.5 }}>
            <Typography sx={{ fontWeight: 700, mb: 2, fontSize: '0.9rem' }}>Milestones</Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {MILESTONES.map((m) => {
                const unlocked = bestCurrent >= m.days;
                return (
                  <Box key={m.days} sx={{
                    display: 'flex', alignItems: 'center', gap: 2, p: 1.5, borderRadius: 2,
                    background: unlocked ? 'rgba(74,222,128,0.08)' : 'transparent',
                    border: unlocked ? '1px solid rgba(74,222,128,0.2)' : '1px solid rgba(255,255,255,0.05)',
                    opacity: unlocked ? 1 : 0.55,
                  }}>
                    <Typography sx={{ fontSize: '1.3rem' }}>{m.emoji}</Typography>
                    <Box sx={{ flex: 1 }}>
                      <Typography sx={{ fontSize: '0.88rem', fontWeight: 600, color: unlocked ? '#e8f4f8' : '#7fa8c8' }}>{m.label}</Typography>
                      <Typography sx={{ fontSize: '0.72rem', color: '#7fa8c8' }}>{m.days} day streak</Typography>
                    </Box>
                    <Chip
                      label={unlocked ? '✅ Unlocked' : `${m.days - bestCurrent} to go`}
                      size="small"
                      sx={{
                        background: unlocked ? 'rgba(74,222,128,0.15)' : 'rgba(255,255,255,0.05)',
                        color: unlocked ? '#4ade80' : '#7fa8c8',
                        fontSize: '0.7rem', height: 22, fontWeight: 600,
                      }}
                    />
                  </Box>
                );
              })}
            </Box>
          </Box>
        </>
      )}

      {/* New Streak Modal */}
      <Dialog open={newStreakOpen} onClose={() => setNewStreakOpen(false)}
        PaperProps={{ sx: { background: '#0d1f3c', border: '1px solid rgba(74,222,128,0.2)', borderRadius: 3, width: '90%', maxWidth: 400 } }}>
        <DialogTitle sx={{ color: '#e8f4f8', fontWeight: 700, pb: 1 }}>Start a New Streak 🔥</DialogTitle>
        <DialogContent>
          <Typography sx={{ color: '#7fa8c8', fontSize: '0.82rem', mb: 2 }}>Pick a preset or type your own:</Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
            {PRESET_TYPES.map((type) => (
              <Chip key={type} label={type} onClick={() => setNewStreakType(type)}
                sx={{
                  background: newStreakType === type ? 'rgba(74,222,128,0.2)' : 'rgba(255,255,255,0.05)',
                  color: newStreakType === type ? '#4ade80' : '#7fa8c8',
                  border: newStreakType === type ? '1px solid rgba(74,222,128,0.4)' : '1px solid rgba(255,255,255,0.1)',
                  cursor: 'pointer', fontWeight: newStreakType === type ? 700 : 400,
                }}
              />
            ))}
          </Box>
          <TextField fullWidth placeholder="Or type a custom habit..." value={newStreakType}
            onChange={(e) => setNewStreakType(e.target.value)}
            sx={{
              '& .MuiOutlinedInput-root': {
                color: 'white',
                '& fieldset': { borderColor: 'rgba(255,255,255,0.1)' },
                '&:hover fieldset': { borderColor: '#4ade80' },
                '&.Mui-focused fieldset': { borderColor: '#4ade80' },
              },
              '& .MuiInputBase-input::placeholder': { color: 'rgba(255,255,255,0.3)' },
            }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button onClick={() => setNewStreakOpen(false)} sx={{ color: '#7fa8c8', textTransform: 'none' }}>Cancel</Button>
          <Button onClick={handleCreateStreak} disabled={!newStreakType.trim() || creating}
            sx={{ background: 'linear-gradient(135deg, #4ade80, #22d3ee)', color: '#0a1628', fontWeight: 700, borderRadius: 2, textTransform: 'none', px: 3 }}>
            {creating ? <CircularProgress size={18} sx={{ color: '#0a1628' }} /> : 'Start Streak'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}